import React, { useMemo, useState } from 'react';
import {
    Box,
    Checkbox,
    Collapse,
    FormControl,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    Typography,
    useTheme,
} from '@mui/material';
import {
    ExpandLess,
    ExpandMore,
    PlaceOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { Location, LocationArea } from '../../../models/filters';

interface LocationSelectorProps {
    locations: Location[];
    locationAreas: LocationArea[];
    selectedLocations: string[];
    onLocationChange: (locationIds: string[]) => void;
}

const LocationSelector = ({
    locations,
    locationAreas,
    selectedLocations,
    onLocationChange,
}: LocationSelectorProps) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const [expandedAreas, setExpandedAreas] = useState<
        Record<string, boolean>
    >({});

    const [selectedEmptyAreas, setSelectedEmptyAreas] =
        useState<string[]>([]);

    const locationsByArea = useMemo(
        () =>
            locations.reduce<Record<string, Location[]>>(
                (acc, loc) => {
                    const areaId =
                        loc.locationAreaId || 'default';

                    if (!acc[areaId]) {
                        acc[areaId] = [];
                    }

                    acc[areaId].push(loc);

                    return acc;
                },
                {}
            ),
        [locations]
    );

    const unassignedLocations =
        locationsByArea.default || [];

    const emptyAreaIds = useMemo(
        () =>
            locationAreas
                .filter(
                    (area) =>
                        (
                            locationsByArea[
                                area.id
                            ] || []
                        ).length === 0
                )
                .map((area) => area.id),
        [locationAreas, locationsByArea]
    );

    const locationString = useMemo(() => {
        const presentationSelectionCount =
            selectedLocations.length +
            selectedEmptyAreas.length;

        if (presentationSelectionCount === 0) {
            return t(
                'screens.dashboarding.landing.noLocations'
            );
        }

        if (
            presentationSelectionCount ===
            locations.length +
                emptyAreaIds.length
        ) {
            return t(
                'screens.dashboarding.landing.allLocations'
            );
        }

        if (presentationSelectionCount === 1) {
            const location = locations.find(
                (loc) =>
                    loc.id === selectedLocations[0]
            );

            if (location) {
                return location.name;
            }

            const area = locationAreas.find(
                (a) =>
                    a.id === selectedEmptyAreas[0]
            );

            return (
                area?.name ||
                t(
                    'screens.dashboarding.landing.noLocations'
                )
            );
        }

        return t(
            'screens.dashboarding.landing.multipleLocations',
            {
                count: presentationSelectionCount,
            }
        );
    }, [
        locations,
        locationAreas,
        selectedLocations,
        selectedEmptyAreas,
        emptyAreaIds,
        t,
    ]);

    const handleToggleAll = () => {
        const allLocationsSelected =
            selectedLocations.length ===
            locations.length;

        const allEmptyAreasSelected =
            selectedEmptyAreas.length ===
            emptyAreaIds.length;

        const allSelected =
            allLocationsSelected &&
            allEmptyAreasSelected;

        if (allSelected) {
            onLocationChange([]);
            setSelectedEmptyAreas([]);
        } else {
            onLocationChange(
                locations.map((loc) => loc.id)
            );

            setSelectedEmptyAreas(emptyAreaIds);
        }
    };

    const handleToggleLocation = (
        locationId: string
    ) => {
        const isSelected =
            selectedLocations.includes(locationId);

        if (isSelected) {
            onLocationChange(
                selectedLocations.filter(
                    (id) => id !== locationId
                )
            );
        } else {
            onLocationChange([
                ...selectedLocations,
                locationId,
            ]);
        }
    };

    const handleToggleArea = (
        areaId: string,
        areaLocations: Location[]
    ) => {
        const isEmptyArea =
            areaLocations.length === 0;

        if (isEmptyArea) {
            const isSelected =
                selectedEmptyAreas.includes(
                    areaId
                );

            if (isSelected) {
                setSelectedEmptyAreas(
                    selectedEmptyAreas.filter(
                        (id) => id !== areaId
                    )
                );
            } else {
                setSelectedEmptyAreas([
                    ...selectedEmptyAreas,
                    areaId,
                ]);
            }

            return;
        }

        const areaLocationIds = areaLocations.map(
            (loc) => loc.id
        );

        const allSelected =
            areaLocationIds.every((id) =>
                selectedLocations.includes(id)
            );

        if (allSelected) {
            onLocationChange(
                selectedLocations.filter(
                    (id) =>
                        !areaLocationIds.includes(id)
                )
            );
        } else {
            onLocationChange(
                Array.from(
                    new Set([
                        ...selectedLocations,
                        ...areaLocationIds,
                    ])
                )
            );
        }
    };

    const toggleAccordion = (areaId: string) => {
        setExpandedAreas((prev) => ({
            ...prev,
            [areaId]: !prev[areaId],
        }));
    };

    const allSelected =
        locations.length > 0 &&
        selectedLocations.length ===
            locations.length &&
        selectedEmptyAreas.length ===
            emptyAreaIds.length;

    return (
        <FormControl
            sx={{
                width: '280px',
                backgroundColor:
                    theme.palette.background.paper,
                borderRadius: '4px',
            }}
        >
            <Select
                multiple
                size="small"
                displayEmpty
                value={[
                    ...selectedLocations,
                    ...selectedEmptyAreas,
                ]}
                input={<OutlinedInput />}
                renderValue={() => (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                        }}
                    >
                        <PlaceOutlined
                            sx={{
                                fontSize: '1.2rem',
                            }}
                        />

                        <span>{locationString}</span>
                    </Box>
                )}
                MenuProps={{
                    anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                    },
                    transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                    },
                    slotProps: {
                        paper: {
                            style: {
                                minWidth: '280px',
                                maxHeight: '320px',
                                overflow: 'overlay',
                            },
                            sx: {
                                '&::-webkit-scrollbar': {
                                    width: '8px',
                                },
                                '&::-webkit-scrollbar-track': {
                                    background: 'transparent',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    background:
                                        theme.palette.grey[400],
                                    borderRadius: '4px',
                                },
                            },
                        },
                    },
                }}
                sx={{
                    '& .MuiOutlinedInput-input': {
                        padding: '8px 12px',
                    },
                }}
            >
                <MenuItem onClick={handleToggleAll}>
                    <Checkbox checked={allSelected} />

                    <ListItemText
                        primary={t(
                            'screens.dashboarding.landing.allLocations'
                        )}
                    />
                </MenuItem>

                {locationAreas.map((area) => {
                    const areaLocations =
                        locationsByArea[area.id] || [];

                    const expanded =
                        expandedAreas[area.id];

                    const allAreaSelected =
                        areaLocations.length === 0
                            ? selectedEmptyAreas.includes(
                                  area.id
                              )
                            : areaLocations.every(
                                  (loc) =>
                                      selectedLocations.includes(
                                          loc.id
                                      )
                              );

                    return (
                        <Box key={area.id}>
                            <MenuItem
                                onClick={() =>
                                    handleToggleArea(
                                        area.id,
                                        areaLocations
                                    )
                                }
                                sx={{
                                     backgroundColor: theme.palette.background.paper,
                                    borderTop:
                                        '1px solid',
                                    borderColor:
                                        'divider',
                                }}
                            >
                                <Checkbox
                                    checked={
                                        allAreaSelected
                                    }
                                />

                                <ListItemText
                                    primary={
                                        <Typography
                                            fontWeight={
                                                600
                                            }
                                            
                                        >
                                            {area.name}
                                        </Typography>
                                    }
                                />

                                {areaLocations.length >
                                    0 && (
                                    <Box
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleAccordion(
                                                area.id
                                            );
                                        }}
                                        sx={{
                                            display: 'flex',
                                            alignItems:
                                                'center',
                                        }}
                                    >
                                        {expanded ? (
                                            <ExpandLess />
                                        ) : (
                                            <ExpandMore />
                                        )}
                                    </Box>
                                )}
                            </MenuItem>

                            {areaLocations.length >
                                0 && (
                                <Collapse
                                    in={expanded}
                                    timeout="auto"
                                    unmountOnExit
                                >
                                    {areaLocations.map(
                                        (location) => {
                                            const isSelected =
                                                selectedLocations.includes(
                                                    location.id
                                                );

                                            return (
                                                <MenuItem
                                                    key={
                                                        location.id
                                                    }
                                                    onClick={() =>
                                                        handleToggleLocation(
                                                            location.id
                                                        )
                                                    }
                                                    sx={{
                                                        pl: 5,
                                                    }}
                                                >
                                                    <Checkbox
                                                        checked={
                                                            isSelected
                                                        }
                                                    />

                                                    <ListItemText
                                                        primary={
                                                            location.name
                                                        }
                                                    />
                                                </MenuItem>
                                            );
                                        }
                                    )}
                                </Collapse>
                            )}
                        </Box>
                    );
                })}

                {unassignedLocations.map(
                    (location) => {
                        const isSelected =
                            selectedLocations.includes(
                                location.id
                            );

                        return (
                            <MenuItem
                                key={location.id}
                                onClick={() =>
                                    handleToggleLocation(
                                        location.id
                                    )
                                }
                                sx={{
                                    pl: 2,
                                }}
                            >
                                <Checkbox
                                    checked={isSelected}
                                />

                                <ListItemText
                                    primary={
                                        location.name
                                    }
                                />
                            </MenuItem>
                        );
                    }
                )}
            </Select>
        </FormControl>
    );
};

export default LocationSelector;
